import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin, DatePicker, message } from 'antd';
import moment from 'moment';
import { getAllClosedAccounts } from "./SavingService";
import { exportClosedAccountsExcelReport } from "../../Utils/ClosedAccountExcelExport";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ClosedAccounts = ({
                            loginPlaceId,
                            districtData,
                            districtName,
                            divisionalId,
                            branchId
                        }) => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedDistricts, setSelectedDistricts] = useState([]);
    const [selectedDivisionalAreas, setSelectedDivisionalAreas] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);

    // Initialize selections based on loginPlaceId
    useEffect(() => {
        if (loginPlaceId === "1" && branchId) setSelectedBranches([parseInt(branchId)]);
        else if (loginPlaceId === "2" && divisionalId) setSelectedDivisionalAreas([parseInt(divisionalId)]);
        else if (loginPlaceId === "4" && districtName) {
            const userDistrict = districtData.find(d => d.districtName === districtName);
            if (userDistrict) setSelectedDistricts([userDistrict.districtId]);
        }
    }, [loginPlaceId, branchId, divisionalId, districtName, districtData]);

    // Update available divisional areas when districts change
    useEffect(() => {
        let filteredDivAreas = [];
        if (loginPlaceId === "2" && divisionalId) {
            filteredDivAreas = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => div.divAId === parseInt(divisionalId));
        } else {
            if (selectedDistricts.length > 0) {
                filteredDivAreas = districtData
                    .filter(d => selectedDistricts.includes(d.districtId))
                    .flatMap(d => d.divisionalAreaDtoList || []);
            } else {
                filteredDivAreas = districtData.flatMap(d => d.divisionalAreaDtoList || []);
            }
        }
        setAvailableDivisionalAreas(filteredDivAreas);
    }, [selectedDistricts, districtData, loginPlaceId, divisionalId]);

    // Update available branches when divisional areas change
    useEffect(() => {
        let filteredBranches = [];
        if (loginPlaceId === "1") {
            const userBranch = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .flatMap(div => div.branchDtoList || [])
                .find(b => b.id === parseInt(branchId));
            if (userBranch) filteredBranches = [userBranch];
        } else if (loginPlaceId === "2") {
            if (selectedDivisionalAreas.length > 0) {
                filteredBranches = districtData
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .filter(div => selectedDivisionalAreas.includes(div.divAId))
                    .flatMap(div => div.branchDtoList || []);
            } else {
                filteredBranches = districtData
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .filter(div => div.divAId === parseInt(divisionalId))
                    .flatMap(div => div.branchDtoList || []);
            }
        } else {
            if (selectedDivisionalAreas.length > 0) {
                filteredBranches = districtData
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .filter(div => selectedDivisionalAreas.includes(div.divAId))
                    .flatMap(div => div.branchDtoList || []);
            } else if (selectedDistricts.length > 0) {
                filteredBranches = districtData
                    .filter(d => selectedDistricts.includes(d.districtId))
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .flatMap(div => div.branchDtoList || []);
            } else {
                filteredBranches = districtData
                    .flatMap(d => d.divisionalAreaDtoList || [])
                    .flatMap(div => div.branchDtoList || []);
            }
        }
        setAvailableBranches(filteredBranches);
    }, [selectedDivisionalAreas, selectedDistricts, districtData, loginPlaceId, divisionalId, branchId]);

    const handleDateRangeChange = (dates) => {
        if (dates && dates[0] && dates[1]) {
            setStartDate(dates[0]);
            setEndDate(dates[1]);
        } else {
            setStartDate(null);
            setEndDate(null);
        }
    };

    const handleDistrictChange = (districts) => {
        if (districts.includes('ALL')) {
            if (loginPlaceId === "4") {
                const userDistrict = districtData.find(d => d.districtName === districtName);
                if (userDistrict) setSelectedDistricts([userDistrict.districtId]);
            } else {
                setSelectedDistricts(districtData.map(d => d.districtId));
            }
        } else {
            setSelectedDistricts(districts);
        }
        setSelectedDivisionalAreas([]);
        setSelectedBranches([]);
    };

    const handleDivAreaChange = (divAreas) => {
        if (divAreas.includes('ALL')) setSelectedDivisionalAreas(availableDivisionalAreas.map(d => d.divAId));
        else setSelectedDivisionalAreas(divAreas);
        setSelectedBranches([]);
    };

    const handleBranchChange = (branches) => {
        if (branches.includes('ALL')) setSelectedBranches(availableBranches.map(b => b.id));
        else setSelectedBranches(branches);
    };

    const generateClosedAccountsReport = async () => {
        if (!startDate || !endDate) {
            message.warning('Please select date range');
            return;
        }

        let branchIdList = [];
        if (loginPlaceId === "1") branchIdList = [parseInt(branchId)];
        else if (selectedBranches.length > 0) branchIdList = selectedBranches.map(id => parseInt(id));
        else branchIdList = availableBranches.map(b => b.id);

        if (branchIdList.length === 0) {
            message.warning('No branches available for selection');
            return;
        }

        setLoading(true);

        try {
            console.log('Starting closed accounts report generation...');
            console.log('Branch ID List:', branchIdList);

            // Create request data with the list of branches
            const requestData = {
                branchIdList: branchIdList,
                startDate: startDate.toDate(),
                endDate: endDate.toDate()
            };

            const response = await getAllClosedAccounts(requestData);
            const responseData = response.data || response;

            if (!Array.isArray(responseData)) {
                throw new Error('Unexpected response format from server');
            }

            // Optimize mapping performance using a map for branch names
            const branchNameMap = new Map(availableBranches.map(b => [b.id, b.branchName]));
            
            const allClosedAccounts = responseData.map(acc => ({
                ...acc,
                branchName: branchNameMap.get(acc.branchId) || acc.branchName || `Branch ${acc.branchId}`,
                branchId: acc.branchId
            }));

            console.log('Total closed accounts collected:', allClosedAccounts.length);

            if (allClosedAccounts.length === 0) {
                message.info('No closed accounts found for the selected criteria');
                setLoading(false);
                return;
            }

            console.log('Generating Excel report...');
            await exportClosedAccountsExcelReport({
                data: allClosedAccounts,
                selectedBranches: availableBranches.filter(b => branchIdList.includes(b.id)).map(b => b.branchName),
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                reportType: 'detailed'
            });

            message.success(`Closed Accounts Report generated successfully! Found ${allClosedAccounts.length} accounts.`);
        } catch (err) {
            console.error('Error generating closed accounts report:', err);
            console.error('Error details:', {
                message: err.message,
                response: err.response,
                stack: err.stack
            });
            message.error('Error generating report: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const disabledDate = (current) => current && current > moment().endOf('day');

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]}>

                        {(loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: '100%' }}
                                        value={selectedDistricts}
                                        onChange={handleDistrictChange}
                                        disabled={loginPlaceId === "4"}
                                    >
                                        {loginPlaceId === "4" ? (
                                            districtData.filter(d => d.districtName === districtName).map(d => (
                                                <Option key={d.districtId} value={d.districtId}>🏙️ {d.districtName}</Option>
                                            ))
                                        ) : (
                                            <>
                                                <Option value="ALL">🔘 Select All Districts</Option>
                                                {districtData.map(d => (
                                                    <Option key={d.districtId} value={d.districtId}>🏙️ {d.districtName}</Option>
                                                ))}
                                            </>
                                        )}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {(loginPlaceId === "2" || loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Select Divisional Areas">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select divisional areas"
                                        style={{ width: '100%' }}
                                        value={selectedDivisionalAreas}
                                        onChange={handleDivAreaChange}
                                        disabled={availableDivisionalAreas.length === 0 || loginPlaceId === "2"}
                                    >
                                        <Option value="ALL">🔘 Select All Divisional Areas</Option>
                                        {availableDivisionalAreas.map(d => (
                                            <Option key={d.divAId} value={d.divAId}>🏙️ {d.divAIdName}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        <Col span={24}>
                            <Form.Item label="Select Branches">
                                <Select
                                    mode="multiple"
                                    placeholder="Select branches"
                                    style={{ width: '100%' }}
                                    value={selectedBranches}
                                    onChange={handleBranchChange}
                                    disabled={availableBranches.length === 0 || loginPlaceId === "1"}
                                >
                                    <Option value="ALL">🔘 Select All Branches</Option>
                                    {availableBranches.map(branch => (
                                        <Option key={branch.id} value={branch.id}>🏢 {branch.branchName}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Select Date Range (Closed Date)">
                                <RangePicker
                                    placeholder={['Start Date', 'End Date']}
                                    style={{ width: '100%' }}
                                    value={startDate && endDate ? [startDate, endDate] : []}
                                    onChange={handleDateRangeChange}
                                    format="YYYY-MM-DD"
                                    disabledDate={disabledDate}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    type="primary"
                                    onClick={generateClosedAccountsReport}
                                    disabled={loading || !startDate || !endDate || availableBranches.length === 0}
                                    className="generate-btn"
                                    size="large"
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>

                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default ClosedAccounts;